import React, {useEffect, useRef, useState} from 'react';
import { ColumnLayout } from '@jpmuitk/column-layout';
import { Card } from '@jpmuitk/card';
import { initAisAccount, initAisConsent, notifyAisLoaded } from '../../redux/clientActions';
import {AIS_COMPONENT_TIMEOUT, AIS_FAILURE, AIS_SUCCESS, AIS_CONSENT_INITIALIZED, SUCCESS} from "../JwtConstants";
import {useMediaQuery} from "@material-ui/core";
import AisSpinner from "./AisSpinner";
import { v4 as UUID } from 'uuid';
import LoadingScreen from './LoadingScreen';

function getSelectedLanguage() {
  return localStorage.getItem('language');
}

const languageMap = {
  en: 'en_US', // English
  de: 'de_DE', // German
  nl: 'nl_NL', // Dutch
}

export default function AisConsentFrame({
  accessToken,
  sessionToken,
  onResponse
}) {
  const [open, setOpen] = useState('open');
  const isSmallScreen = useMediaQuery('(max-width:450px)');
  const slaTimeoutMs = 600000; // 10 minutes
  const [selectedLanguage, setSelectedLanguage] = useState(languageMap[getSelectedLanguage() || "en"] || 'en_US');
  const [isAisUiReady, setIsAisUiReady] = useState(false);
  const [isAccountBeingFetched, setIsAccountBeingFetched] = useState(false);
  const [hasSpinnerTimeoutOccurred, setHasSpinnerTimeoutOccurred] = useState(false);
  const hasSpinnerTimeoutOccurredRef = useRef(hasSpinnerTimeoutOccurred);
  const [retiredIds, setRetiredIds] = useState([]);
  const retiredIdsRef = useRef(retiredIds);
  const frameId = useRef();
  const isActiveRef = useRef(true);
  const aisLoadedSignalSentRef = useRef(false);

  const revealAisUi = () => {
    if (hasSpinnerTimeoutOccurredRef.current) {
      return;
    }
    if (!aisLoadedSignalSentRef.current) {
      aisLoadedSignalSentRef.current = true;
      notifyAisLoaded(accessToken, sessionToken).catch((error) =>
        console.error('[AisConsentFrame] Failed to send AIS loaded signal', error));
    }
    setIsAisUiReady(true);
  };

  useEffect(() => {
    if (isAccountBeingFetched) {
      console.log('[AisConsentFrame] Account is being fetched, setting isActiveRef to false and skipping timer setup');
      isActiveRef.current = false;
      return;
    }

    const startTime = Date.now();
    console.log('[AisConsentFrame] Starting 10 minute SLA timeout and interval timer');
    const timerId = setTimeout(() => {
      console.log('[AisConsentFrame] SLA timeout fired after 10 minutes, setting isActiveRef to false');
      isActiveRef.current = false;
      onResponse({ status: AIS_COMPONENT_TIMEOUT });
    }, slaTimeoutMs);

    let lastLogTime = startTime;
    const intervalId = setInterval(() => {
      if (!isActiveRef.current) {
        console.log('[AisConsentFrame] isActiveRef is false, clearing interval');
        clearInterval(intervalId);
        return;
      }
      const now = Date.now();
      const elapsed = now - startTime;
      const timeLeft = Math.max(0, Math.ceil((slaTimeoutMs - elapsed) / 1000));
      if (now - lastLogTime >= 30000) {
        console.log('[AisConsentFrame] Interval tick - time left:', timeLeft, 'seconds');
        lastLogTime = now;
      }
      if (timeLeft <= 0) {
        console.log('[AisConsentFrame] Time left reached 0, clearing interval');
        clearInterval(intervalId);
      }
    }, 1000);

    return () => {
      console.log('[AisConsentFrame] Cleanup: clearing timeout and interval');
      clearTimeout(timerId);
      clearInterval(intervalId);
    };
  }, [slaTimeoutMs, isAccountBeingFetched]);

  useEffect(() => {
    const storageListener = (event) => {
      const updatedLanguage = languageMap[getSelectedLanguage() || "en"] || 'en_US';
      if (updatedLanguage !== selectedLanguage) {
        setSelectedLanguage(updatedLanguage);
      }
    };

    window.addEventListener('storage', storageListener);
    return () => window.removeEventListener('storage', storageListener);
  }, []);

  useEffect(() => {
    consent().then();
  }, [selectedLanguage]);

  useEffect(() => {
    hasSpinnerTimeoutOccurredRef.current = hasSpinnerTimeoutOccurred;
  }, [hasSpinnerTimeoutOccurred]);

  useEffect(() => {
    retiredIdsRef.current = retiredIds;
  }, [retiredIds]);

  const handleSpinnerTimeout = () => {
    setHasSpinnerTimeoutOccurred(true);
    setRetiredIds((prevRetiredIds) => [...prevRetiredIds, frameId.current]);
  };

  const consent = async () => {
    try {
      setIsAccountBeingFetched(false);
      const staticFrameId = UUID();
      frameId.current = staticFrameId;
      const aisConsent = await initAisConsent(accessToken, sessionToken, selectedLanguage);
      if (aisConsent?.aisConsentInitiationResponse?.consentLink != null) {
        if (!retiredIdsRef.current.includes(staticFrameId)) {
          handleConsentUrl(aisConsent?.aisConsentInitiationResponse?.consentLink, aisConsent?.aisConsentInitiationResponse?.consentId
            ?? aisConsent?.aisConsentInitiationResponse?.id);
          onResponse({ status: AIS_CONSENT_INITIALIZED, description: null });
        }
      } else {
        console.error('Error: Consent link is not available');
        console.log('[AisConsentFrame] Consent link unavailable, setting isActiveRef to false to stop timer');
        isActiveRef.current = false;
        if (aisConsent?.avsResponse?.codes?.error) {
          onResponse({ status: AIS_FAILURE, code: aisConsent.avsResponse.codes.error.code, description: aisConsent.avsResponse.codes.error.description });
        } else {
          onResponse({ status: AIS_FAILURE });
        }
      }
      return aisConsent;
    } catch (error) {
      console.error(error);
      console.log('[AisConsentFrame] Consent call failed, setting isActiveRef to false to stop timer');
      isActiveRef.current = false;
      onResponse({ status: AIS_FAILURE });
    }
  };

  const handleConsentUrl = (url, consentId) => {
    // US (Mastercard/Finicity) emits onDone/onError; non-US emits onSuccess/onFailed. Share one behavior.
    const handleLinkSuccess = (event) => {
      console.log('Yay! Account Link Successfully', event);
      console.log('[AisConsentFrame] Account linked, setting isActiveRef to false to stop timer');
      isActiveRef.current = false;
      setOpen(false);
      setIsAccountBeingFetched(true);
      handleAccountsCall(consentId)
        .then((result) => {
          onResponse({ status: result });
        });
    };
    const handleLinkFailure = (event) => {
      console.error('Account Linking Failed');
      console.log('[AisConsentFrame] Failed, setting isActiveRef to false to stop timer');
      isActiveRef.current = false;
      onResponse({ status: AIS_FAILURE, code: 9001, description: "AIS Failed: " + event.message });
    };
    if (url) {
      window.GlobalConnect.open(url,
        {
          onSuccess: handleLinkSuccess,
          onDone: handleLinkSuccess,
          onCancel: (event) => {
            console.log('Account Linking Cancelled');
            console.log('[AisConsentFrame] Cancelled, setting isActiveRef to false to stop timer');
            isActiveRef.current = false;
            onResponse({ status: AIS_FAILURE, code: 8615, description: "Customer Canceled" });
          },
          onFailed: handleLinkFailure,
          onError: handleLinkFailure,
          onAbandoned: (event) => {
            console.error('Account Linking Abandoned');
            console.log('[AisConsentFrame] Abandoned, setting isActiveRef to false to stop timer');
            isActiveRef.current = false;
            onResponse({ status: AIS_FAILURE, code: 9001, description: "AIS Failed: " + event.message });
          },
          onLinkExpired: (event) => {
            console.error('Account Link Expired');
            console.log('[AisConsentFrame] Link expired, setting isActiveRef to false to stop timer');
            isActiveRef.current = false;
            onResponse({ status: AIS_FAILURE, code: 9001, description: "AIS Failed: " + event.message });
          },
          onRoute: (event) => console.log('Navigated to:', event.screen),
          onUser: (event) => console.log('User action:', event.action),
          onVendorEvent: (event) => console.log('Vendor event:', event),
          onLoad: () => {
            console.log('[AisConsentFrame] SDK onLoad fired');
            revealAisUi();
          },
          onAcknowledged: (event) => {
            console.log('Acknowledged:', event);
            revealAisUi();
          },
          onClose: () => console.log('SDK Closed'),
        },
        {
          selector: "#connect-container",
          overlay: '#ffffff',
          popup: false,
        }
      );
    }
      setOpen(true);
  };

  const handleAccountsCall = async (consentId) => {
    try {
      const accountsResponse = await initAisAccount(accessToken, sessionToken, consentId);
      if (accountsResponse?.result === SUCCESS) {
        return AIS_SUCCESS;
      } else {
        return AIS_FAILURE;
      }
    } catch (error) {
      console.error(error);
      return AIS_FAILURE;
    }
  };

  const handleRetry = () => {
    setHasSpinnerTimeoutOccurred(false);
    setIsAisUiReady(false);
    consent().then();
  };

  return (
    !isAccountBeingFetched ?
      <Card
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: isSmallScreen ? '90vw' : '40vw',
          height: '82vh'
        }}
      >
        <ColumnLayout container spacing={3} justify="center" alignItems="center" style={{width: isSmallScreen ? "100vw" : "40vw", minHeight: 450}}>
          <ColumnLayout item xs={12}>
            {!isAisUiReady ?
              <AisSpinner
                sessionToken={sessionToken}
                accessToken={accessToken}
                setSpinnerTimeoutOccurred={handleSpinnerTimeout}
                retryBankRedirect={handleRetry}
              />
              : null}
            <div
              id="connect-container"
              style={{
                position: "relative",
                height: open ? '80vh' : '0%',
                width: '100vw',
                maxWidth: '100%',
                boxSizing: 'border-box',
                display: isAisUiReady ? 'block' : 'none',
              }}
            />
          </ColumnLayout>
        </ColumnLayout>
      </Card>
      :
      <LoadingScreen/>
  );
}
